import { useCallback, useEffect, useState } from 'react';
import type { OnDocumentLoadSuccess } from 'react-pdf/dist/cjs/shared/types';

import { useStablePdfUrl } from '@/hooks/useStablePdfUrl';

import type { ESignPageDimensions } from './types';

interface UseESignPdfDocumentParams {
  url: string | null | undefined;
}

interface UseESignPdfDocumentResult {
  activeUrl: string;
  pdfKey: number;
  numPages: number;
  isLoading: boolean;
  pageDimensionsMap: Map<number, ESignPageDimensions>;
  onDocumentLoadSuccess: OnDocumentLoadSuccess;
  onDocumentLoadError: () => void;
  onPageRenderSuccess: (
    pageNumber: number,
    width: number,
    height: number,
    sourceWidth: number,
    sourceHeight: number
  ) => void;
  errorRetryCountRef: React.MutableRefObject<number>;
}

export function useESignPdfDocument({
  url
}: UseESignPdfDocumentParams): UseESignPdfDocumentResult {
  const { activeUrl, pdfKey, onLoadSuccess, onLoadError, errorRetryCountRef } =
    useStablePdfUrl(url);

  const [numPages, setNumPages] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [pageDimensionsMap, setPageDimensionsMap] = useState<
    Map<number, ESignPageDimensions>
  >(new Map());

  useEffect(() => {
    if (!activeUrl) {
      return;
    }

    setIsLoading(true);
    setNumPages(0);
    setPageDimensionsMap(new Map());
  }, [activeUrl]);

  const onDocumentLoadSuccess: OnDocumentLoadSuccess = useCallback(
    (document) => {
      onLoadSuccess();
      setNumPages(document.numPages);
      setIsLoading(false);
    },
    [onLoadSuccess]
  );

  const onDocumentLoadError = useCallback(() => {
    onLoadError();
    setIsLoading(false);
  }, [onLoadError]);

  const onPageRenderSuccess = useCallback(
    (
      pageNumber: number,
      width: number,
      height: number,
      sourceWidth: number,
      sourceHeight: number
    ) => {
      setPageDimensionsMap((prev) => {
        const existing = prev.get(pageNumber);
        if (
          existing &&
          existing.renderedWidth === width &&
          existing.renderedHeight === height &&
          existing.sourceWidth === sourceWidth &&
          existing.sourceHeight === sourceHeight
        ) {
          return prev;
        }
        const next = new Map(prev);
        next.set(pageNumber, {
          pageNumber,
          renderedWidth: width,
          renderedHeight: height,
          sourceWidth,
          sourceHeight
        });
        return next;
      });
    },
    []
  );

  return {
    activeUrl,
    pdfKey,
    numPages,
    isLoading,
    pageDimensionsMap,
    onDocumentLoadSuccess,
    onDocumentLoadError,
    onPageRenderSuccess,
    errorRetryCountRef
  };
}
